import { useState } from "react";
import { Badge, MUTED_TEXT, stackStyle } from "@dayvise/react-ui";

export interface ProvenanceNode {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  readonly scope: "private" | "shared" | "source";
}

export interface ProvenanceEdge {
  readonly from: string;
  readonly to: string;
  readonly label: string;
}

/** Recorded links only; matching text never creates an edge. */
export function filterProvenance(nodes: readonly ProvenanceNode[], edges: readonly ProvenanceEdge[], query: string) {
  const needle = query.trim().toLocaleLowerCase();
  const matches = nodes.filter(node => !needle || `${node.label} ${node.detail} ${node.scope}`.toLocaleLowerCase().includes(needle));
  const selected = new Set(matches.map(node => node.id));
  // Keep direct neighbours so a filtered memory retains its recorded source.
  if (needle) for (const edge of edges) {
    if (matches.some(node => node.id === edge.from || node.id === edge.to)) {
      selected.add(edge.from);
      selected.add(edge.to);
    }
  }
  const visible = nodes.filter(node => selected.has(node.id)).slice(0, 80);
  const ids = new Set(visible.map(node => node.id));
  return { nodes: visible, edges: edges.filter(edge => ids.has(edge.from) && ids.has(edge.to)), total: selected.size };
}

export function ProvenanceGraph({ nodes, edges }: { readonly nodes: readonly ProvenanceNode[]; readonly edges: readonly ProvenanceEdge[] }) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const graph = filterProvenance(nodes, edges, query);
  const selected = graph.nodes.find(node => node.id === selectedId);
  const positions = new Map(graph.nodes.map((node, index) => [node.id, { x: 130 + (index % 3) * 265, y: 50 + Math.floor(index / 3) * 85 }]));
  const height = Math.max(140, Math.ceil(graph.nodes.length / 3) * 85 + 20);
  const pick = (id: string) => setSelectedId(id);
  return (
    <div style={stackStyle("var(--dayvise-space-sm)")}>
      <label style={stackStyle("var(--dayvise-space-xs)")}>
        Filter loaded provenance
        <input type="search" value={query} onChange={event => { setQuery(event.target.value); setSelectedId(null); }} placeholder="Memory text, source or scope" />
      </label>
      <p style={{ ...MUTED_TEXT, margin: 0 }}>{graph.nodes.length} of {graph.total} matching nodes · select a node for details. Lines show recorded source or target links.</p>
      {graph.nodes.length === 0 ? <p>No recorded nodes match this filter.</p> : (
        <div style={{ maxHeight: 420, overflow: "auto", border: "1px solid var(--dayvise-color-border)", borderRadius: "var(--dayvise-radius-md)" }}>
          <svg viewBox={`0 0 790 ${height}`} style={{ width: "100%", minWidth: 560, height }} role="group" aria-label="Interactive memory provenance graph">
            {graph.edges.map(edge => {
              const from = positions.get(edge.from);
              const to = positions.get(edge.to);
              if (!from || !to) return null;
              return <line key={`${edge.from}:${edge.to}:${edge.label}`} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="var(--dayvise-color-border)" strokeWidth={2}><title>{edge.label}</title></line>;
            })}
            {graph.nodes.map(node => {
              const position = positions.get(node.id)!;
              return (
                <g key={node.id} transform={`translate(${position.x},${position.y})`} role="button" tabIndex={0} aria-label={`${node.scope}: ${node.label}`} aria-pressed={selectedId === node.id} onClick={() => pick(node.id)} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); pick(node.id); } }} style={{ cursor: "pointer" }}>
                  <rect x={-112} y={-28} width={224} height={56} rx={8} fill="var(--dayvise-color-surface)" stroke={selectedId === node.id ? "var(--dayvise-color-accent)" : "var(--dayvise-color-border)"} strokeWidth={selectedId === node.id ? 3 : 1} />
                  <text textAnchor="middle" y={-4} fill="var(--dayvise-color-text-primary)" fontSize={13}>{node.label.length > 28 ? `${node.label.slice(0, 27)}…` : node.label}</text>
                  <text textAnchor="middle" y={15} fill="var(--dayvise-color-text-muted)" fontSize={11}>{node.scope}</text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
      {selected ? <div role="region" aria-label="Selected provenance node" style={stackStyle("var(--dayvise-space-xs)")}><strong>{selected.label}</strong><Badge tone="neutral">{selected.scope}</Badge><p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{selected.detail}</p></div> : null}
      <details>
        <summary>Searchable node list</summary>
        <ul>{graph.nodes.map(node => <li key={node.id}><button type="button" onClick={() => pick(node.id)}>{node.label} · {node.scope}</button></li>)}</ul>
      </details>
    </div>
  );
}
